import { useState } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Settings01Icon,
  CheckmarkCircle01Icon,
  AlertCircleIcon,
  BubbleChatIcon,
  ChevronDown,
  ChevronUp,
  Loading03Icon,
} from "@hugeicons/core-free-icons";
import { cn } from "@/lib/utils";
import type { AgentStep } from "@/lib/stores/execution-store";

interface AgentStepLogProps {
  steps: AgentStep[];
  isRunning?: boolean;
  className?: string;
}

type StepConfig = {
  icon: typeof Settings01Icon;
  label: string;
  iconClass: string;
  rowClass: string;
};

const STEP_CONFIG: Record<string, StepConfig> = {
  tool_call: {
    icon: Settings01Icon,
    label: "Calling",
    iconClass: "text-brand",
    rowClass: "bg-brand/[0.04] border-brand/10",
  },
  tool_result: {
    icon: CheckmarkCircle01Icon,
    label: "Result",
    iconClass: "text-emerald-500",
    rowClass: "bg-emerald-500/[0.04] border-emerald-500/10",
  },
  tool_error: {
    icon: AlertCircleIcon,
    label: "Error",
    iconClass: "text-destructive",
    rowClass: "bg-destructive/[0.04] border-destructive/10",
  },
  thinking: {
    icon: BubbleChatIcon,
    label: "Thinking",
    iconClass: "text-muted-foreground",
    rowClass: "bg-muted/30 border-border/30",
  },
  unknown: {
    icon: Loading03Icon,
    label: "Step",
    iconClass: "text-muted-foreground",
    rowClass: "bg-muted/20 border-border/20",
  },
};

export function AgentStepLog({ steps, isRunning = false, className }: AgentStepLogProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  if (steps.length === 0) return null;

  return (
    <div
      className={cn(
        "rounded-lg border border-border/40 bg-background/60 overflow-hidden text-xs",
        className,
      )}
    >
      {/* Header */}
      <button
        onClick={() => setIsExpanded((v) => !v)}
        className="flex w-full items-center justify-between px-2.5 py-1.5 text-left hover:bg-muted/30 transition-colors"
      >
        <div className="flex items-center gap-1.5">
          {isRunning && (
            <div className="h-1.5 w-1.5 rounded-full bg-brand animate-pulse" />
          )}
          <span className="font-medium text-foreground/70">
            {steps.length} step{steps.length !== 1 ? "s" : ""}
          </span>
        </div>
        <HugeiconsIcon
          icon={isExpanded ? ChevronUp : ChevronDown}
          size={11}
          className="text-muted-foreground"
        />
      </button>

      {/* Steps list */}
      {isExpanded && (
        <div className="border-t border-border/40 divide-y divide-border/25 max-h-[260px] overflow-y-auto">
          {steps.map((step) => {
            const cfg = STEP_CONFIG[step.type] ?? STEP_CONFIG.unknown;
            return (
              <div
                key={step.index}
                className={cn("px-2.5 py-2 space-y-1 border-l-2", cfg.rowClass)}
              >
                {/* Step header row */}
                <div className="flex items-center gap-1.5">
                  <HugeiconsIcon
                    icon={cfg.icon}
                    size={11}
                    className={cfg.iconClass}
                  />
                  <span className="font-semibold text-foreground/80">
                    {cfg.label}
                    {step.tool_name ? (
                      <span className="font-mono text-[10px] ml-1 text-foreground/60">
                        {step.tool_name}
                      </span>
                    ) : null}
                  </span>
                </div>

                {/* Tool args */}
                {step.tool_args && Object.keys(step.tool_args).length > 0 && (
                  <div className="pl-4 font-mono text-[10px] text-muted-foreground/80 truncate">
                    {JSON.stringify(step.tool_args)}
                  </div>
                )}

                {/* Content */}
                {step.content && (
                  <div className="pl-4 text-muted-foreground/75 line-clamp-4 whitespace-pre-wrap break-words leading-relaxed">
                    {step.content}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
